// App2.tsx
import React, { useEffect } from 'react';
import {
  BrowserRouter as Router,
  Switch,
  Route,
  Redirect,
} from 'react-router-dom';
import { Toaster } from 'react-hot-toast';

import Landing from './website/Landing';
import { NavBar } from './website/NavBar';
import About from './website/About';
import Download from './website/Download';
import Partners from './website/Partners';
import Moving from './website/Moving';
import Berkeley from './website/Berkeley';
import CollegeStorage from './website/CollegeStorage';
import Faq from './website/FAQ';
import Contact from './website/Contact';
import Reviews from './website/Reviews';
import Privacy from './website/Privacy';
import BlogIndex from './website/blog/BlogIndex';
import BlogPost from './website/blog/BlogPost';
import SanFrancisco from './website/SanFrancisco';
import Tacoma from './website/Tacoma';
import Students from './website/Students';
import ApartmentStorage from './website/ApartmentStorage';
import Support from './website/Support';
import PublicPrivacy from './website/PublicPrivacy';
import PublicTerms from './website/PublicTerms';
import { Header, Footer } from './components';
import { AuthWrapper } from './context/auth'; // admin auth
import { UsersWrapper } from './context/users';

import {
  Dashboard,
  Orders,
  Customers,
  Invoices,
  Inventory,
  Drivers,
  OrderDetails,
  Billing,
  CalendarPage,
  Ledger,
} from './pages';
import FullPageInvoice from './pages/Invoices/FullPageInvoice';
import theme from './utils/theme';
import { Grommet, Box } from 'grommet';

// Tenant auth + flows
import { TenantAuthProvider } from './context/tenantAuth';
import { RequireTenantAuth } from './components/TenantApp/RequireTenantAuth';
import { TenantUserInfoProvider } from './context/userInfoProvider';
import TenantLoginPage from './tenant/SignupPages/TenantLoginPage';
import TenantPasswordResetPage from './tenant/SignupPages/TenantPasswordResetPage';
import AddressInfoPage from './tenant/SignupPages/AddressInfoPage';
import SelectFacilityPage from './tenant/SignupPages/SelectFacilityPage';
import AccountInfoPage from './tenant/SignupPages/AccountInfoPage';
import ConfirmContactInfoPage from './tenant/SignupPages/ConfirmContactInfoPage';
import InitialAppointmentPage from './tenant/SignupPages/InitialAppointmentPage';
import InitialPickupPage from './tenant/SignupPages/InitialPickupPage';
import BillingInfoPage from './tenant/SignupPages/BillingInfoPage';
import InitialConfirmationPage from './tenant/SignupPages/InitialConfirmationPage';
import ContractPage from './tenant/LegalPages.tsx/ContractPage';
import PrivacyPolicyPage from './tenant/LegalPages.tsx/PrivacyPage';
import TermsPage from './tenant/LegalPages.tsx/TermsPage';
import TenantApp from './tenant/TenantApp';

// --- Admin app (unchanged) ---
const AdminRoutes = (): JSX.Element => (
  <Switch>
    <Route exact path="/Admin">
      <Redirect to="/Admin/dashboard" />
    </Route>
    <Route path="/Admin/dashboard">
      <Dashboard />
    </Route>
    <Route exact path="/Admin/orders">
      <Orders />
    </Route>
    <Route path="/Admin/customers">
      <Customers />
    </Route>
    <Route path="/Admin/invoices">
      <Invoices />
    </Route>
    <Route path="/Admin/inventory">
      <Inventory />
    </Route>
    <Route path="/Admin/drivers">
      <Drivers />
    </Route>
    <Route path="/Admin/calendar">
      <CalendarPage />
    </Route>
    <Route path="/Admin/ledger">
      <Ledger />
    </Route>
    <Route path="/Admin/settings">
      <Billing />
    </Route>
    <Route exact path="/orders/:orderId">
      <OrderDetails />
    </Route>
    <Route exact path="/orders/:orderId/invoice">
      <FullPageInvoice />
    </Route>
  </Switch>
);

const AdminApp = (): JSX.Element => (
  <AuthWrapper>
    <Grommet full theme={theme}>
      <Toaster />
      <UsersWrapper>
        <Box fill direction="column">
          <Header />
          <Box
            flex
            overflow="auto"
            background="light-3"
            as="main"
            pad={{ vertical: 'medium', horizontal: 'medium' }}
          >
            <AdminRoutes />
          </Box>
        </Box>
      </UsersWrapper>
    </Grommet>
  </AuthWrapper>
);

// --- Main app ---
const App2 = (): JSX.Element => {
  useEffect(() => {
    const script = document.createElement('script');
    script.async = true;
    script.src = 'https://www.googletagmanager.com/gtag/js?id=AW-16933443005';
    document.head.appendChild(script);

    const script2 = document.createElement('script');
    script2.innerHTML = `
      window.dataLayer = window.dataLayer || [];
      function gtag(){dataLayer.push(arguments);}
      gtag('js', new Date());
      gtag('config', 'AW-16933443005');
    `;
    document.head.appendChild(script2);
  }, []);

  return (
    <div className="App" style={{ overflowX: 'hidden' }}>
      <Router>
        <NavBar />
        <Switch>
          {/* Marketing */}
          <Route exact path="/">
            <Landing />
          </Route>
          <Route path="/About">
            <About />
          </Route>
          <Route path="/FAQ">
            <Faq />
          </Route>
          <Route path="/Contact">
            <Contact />
          </Route>
          <Route path="/Privacy">
            <PrivacyPolicyPage />
          </Route>
          <Route path="/Download">
            <Download />
          </Route>
          <Route path="/Partners">
            <Partners />
          </Route>
          <Route path="/Reviews">
            <Reviews />
          </Route>
          <Route path="/moving">
            <Moving />
          </Route>
          <Route path="/berkeley">
            <Berkeley />
          </Route>
          <Route path="/college-storage">
            <CollegeStorage />
          </Route>
          <Route exact path="/blog">
            <BlogIndex />
          </Route>
          <Route path="/blog/:slug">
            <BlogPost />
          </Route>

          {/* SEO landing pages */}
          <Route path="/san-francisco">
            <SanFrancisco />
          </Route>
          <Route path="/tacoma">
            <Tacoma />
          </Route>
          <Route path="/students">
            <Students />
          </Route>
          <Route path="/apartment-storage">
            <ApartmentStorage />
          </Route>

          {/* Support & legal (public) */}
          <Route path="/support">
            <Support />
          </Route>
          <Route path="/privacy">
            <PublicPrivacy />
          </Route>
          <Route path="/terms">
            <PublicTerms />
          </Route>

          {/* Tenant signup (public) */}
          <Route path="/signup/select-facility">
            <SelectFacilityPage />
          </Route>
          <Route path="/signup/account-info">
            <AccountInfoPage />
          </Route>
          <Route path="/signup/confirm-contact-info">
            <ConfirmContactInfoPage />
          </Route>
          <Route path="/signup/initial-appointment">
            <InitialAppointmentPage />
          </Route>
          <Route path="/signup/initial-pickup">
            <InitialPickupPage />
          </Route>
          <Route path="/signup/billing-info">
            <BillingInfoPage />
          </Route>
          <Route path="/signup/initial-confirmation">
            <InitialConfirmationPage />
          </Route>
          <Route path="/signup/privacy">
            <PrivacyPolicyPage />
          </Route>
          <Route path="/signup/lease">
            <ContractPage />
          </Route>
          <Route path="/signup/terms">
            <TermsPage />
          </Route>
          <Route path="/signup">
            <AddressInfoPage />
          </Route>

          {/* Tenant area: login + portal under /tenant */}
          <Route path="/tenant">
            <TenantAuthProvider>
              <Switch>

                <Route path="/tenant/password-reset">
                  <TenantPasswordResetPage />
                </Route>
                <Route path="/tenant/login">
                  <TenantLoginPage />
                </Route>

                <Route path="/tenant">
                  <RequireTenantAuth>
                    <TenantUserInfoProvider>
                      <TenantApp />
                    </TenantUserInfoProvider>
                  </RequireTenantAuth>
                </Route>

              </Switch>
            </TenantAuthProvider>
          </Route>

          {/* Admin app – unchanged */}
          <Route path="/Admin">
            <AdminApp />
          </Route>

          {/* catch-all */}
          <Route path="*">
            <Redirect to="/" />
          </Route>
        </Switch>
        <Footer />
      </Router>
    </div>
  );
};

export default App2;
